import { useEffect, useMemo, useRef, useState } from "react";
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, IconButton, InputAdornment, MenuItem, Paper,
    Select, Stack, TextField, Tooltip, Typography, useTheme
} from "@mui/material";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

import NoticeBar from "../products/NoticeBar";
import GetStartedHero from "../products/GetStartedHero";
import {
    deleteContextMesh, listContextMeshes, refreshContextMeshAnalysis,
    type ContextMeshWorkspace
} from "./contextMeshApi";
import {
    discardProductCreationDraft,
    isStandaloneContextMeshCreationDraft,
    listProductCreationDrafts,
    productCreationDraftProgress,
    type ProductCreationDraftRecord
} from "../newProduct/draftApi";
import { ConfirmDialog } from "../newProduct/ConfirmDialog";
import { DRAFT_BLUE, DraftBadge } from "../products/DraftProductCard";

type HubSort = "updated-desc" | "updated-asc" | "name-asc" | "name-desc" | "score-desc" | "score-asc";
type HubStatus = "current" | "processing" | "incomplete" | "failed";
type HubStatusFilter = "all" | HubStatus;

const HUB_ROW_MEDIUM_QUERY = "@container context-mesh-list (max-width: 959px)";
const HUB_ROW_COMPACT_QUERY = "@container context-mesh-list (max-width: 719px)";
const HUB_ROW_PHONE_QUERY = "@container context-mesh-list (max-width: 519px)";

const HUB_ROW_LAYOUT = {
    boxSizing: "border-box",
    display: "grid",
    gridTemplateColumns: "26px minmax(0, 1fr) 110px 60px 150px",
    gridTemplateAreas: '"icon primary updated mesh actions"',
    alignItems: "center",
    columnGap: 1.5,
    height: 52,
    minHeight: 52,
    px: 2.5,
    [HUB_ROW_MEDIUM_QUERY]: {
        gridTemplateColumns: "26px minmax(0, 1fr) 88px 48px 142px",
        columnGap: 1
    },
    [HUB_ROW_COMPACT_QUERY]: {
        gridTemplateColumns: "26px minmax(0, 1fr) 30px auto",
        gridTemplateRows: "28px 20px",
        gridTemplateAreas: '"icon primary mesh actions" "icon meta mesh actions"',
        alignContent: "center",
        columnGap: 1,
        height: 64,
        minHeight: 64,
        px: 2
    }
} as const;

const HUB_PRIMARY_CELL = {
    gridArea: "primary",
    minWidth: 0,
    overflow: "hidden",
    whiteSpace: "nowrap"
} as const;

const HUB_COMPACT_META = {
    display: "none",
    gridArea: "meta",
    minWidth: 0,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    [HUB_ROW_COMPACT_QUERY]: { display: "block" }
} as const;

export interface ContextMeshConsoleProps {
    token?: string | null;
    canCreateMesh?: boolean;
    canViewAnalysis?: boolean;
    refreshSignal?: number;
    highlightedContextMeshId?: string | null;
    onCreateContextMesh: () => void;
    onResumeContextMeshDraft: (draft: ProductCreationDraftRecord) => void;
    onOpenContextMesh: (contextMesh: ContextMeshWorkspace) => void | Promise<void>;
    onEditContextMesh?: (contextMesh: ContextMeshWorkspace) => void;
}

function displayStatus(mesh: ContextMeshWorkspace): HubStatus {
    const summary = mesh.analysisSummary;
    if (summary?.status === "failed") return "failed";
    if (summary?.status === "pending" || summary?.status === "running" || summary?.isStale) return "processing";
    if (summary?.status === "ready" && summary.score != null) return "current";
    return "incomplete";
}

function statusLabel(status: HubStatus): string {
    return status === "current" ? "Current" : status[0].toUpperCase() + status.slice(1);
}

export function ContextMeshConsole({
    token,
    canCreateMesh = true,
    canViewAnalysis = true,
    refreshSignal = 0,
    highlightedContextMeshId,
    onCreateContextMesh,
    onResumeContextMeshDraft,
    onOpenContextMesh,
    onEditContextMesh
}: ContextMeshConsoleProps) {
    const theme = useTheme();
    const highlightedRef = useRef<HTMLDivElement | null>(null);
    const [meshes, setMeshes] = useState<ContextMeshWorkspace[]>([]);
    const [drafts, setDrafts] = useState<ProductCreationDraftRecord[]>([]);
    const [query, setQuery] = useState("");
    const [sort, setSort] = useState<HubSort>("updated-desc");
    const [statusFilter, setStatusFilter] = useState<HubStatusFilter>("all");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshingIds, setRefreshingIds] = useState<Set<string>>(() => new Set());
    const [deletingDraft, setDeletingDraft] = useState<ProductCreationDraftRecord | null>(null);
    const [deletingMesh, setDeletingMesh] = useState<ContextMeshWorkspace | null>(null);
    const [isDeletingMesh, setIsDeletingMesh] = useState(false);

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        Promise.all([listContextMeshes(token), listProductCreationDrafts(token)])
            .then(([items, draftItems]) => {
                if (cancelled) return;
                setMeshes(items);
                setDrafts(draftItems.filter(isStandaloneContextMeshCreationDraft));
            })
            .catch(cause => { if (!cancelled) setError(cause instanceof Error ? cause.message : "Unable to load Context Meshes."); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [refreshSignal, token]);

    useEffect(() => {
        if (highlightedContextMeshId && !loading) highlightedRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, [highlightedContextMeshId, loading]);

    const visibleMeshes = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return meshes.filter(mesh => {
            const matchesQuery = !needle || [mesh.name, mesh.description].some(value => value?.toLowerCase().includes(needle));
            const state = displayStatus(mesh);
            return matchesQuery && (statusFilter === "all" || statusFilter === state);
        }).sort((left, right) => {
            const leftScore = left.analysisSummary?.score ?? -1;
            const rightScore = right.analysisSummary?.score ?? -1;
            const leftUpdated = Date.parse(left.updatedAt ?? "") || 0;
            const rightUpdated = Date.parse(right.updatedAt ?? "") || 0;
            if (sort === "name-asc") return left.name.localeCompare(right.name);
            if (sort === "name-desc") return right.name.localeCompare(left.name);
            if (sort === "score-asc") return leftScore - rightScore;
            if (sort === "score-desc") return rightScore - leftScore;
            if (sort === "updated-asc") return leftUpdated - rightUpdated;
            return rightUpdated - leftUpdated;
        });
    }, [meshes, query, sort, statusFilter]);

    const visibleDrafts = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return drafts
            .filter(draft => {
                const name = draft.draft_state.state.fields.name.trim() || "Untitled Context Mesh";
                return !needle || name.toLowerCase().includes(needle);
            })
            .sort((left, right) => {
                const leftName = left.draft_state.state.fields.name.trim() || "Untitled Context Mesh";
                const rightName = right.draft_state.state.fields.name.trim() || "Untitled Context Mesh";
                const leftUpdated = Date.parse(left.updated_at) || 0;
                const rightUpdated = Date.parse(right.updated_at) || 0;
                if (sort === "name-asc") return leftName.localeCompare(rightName);
                if (sort === "name-desc") return rightName.localeCompare(leftName);
                if (sort === "updated-asc") return leftUpdated - rightUpdated;
                return rightUpdated - leftUpdated;
            });
    }, [drafts, query, sort]);

    const handleRefresh = async (mesh: ContextMeshWorkspace) => {
        if (!token || refreshingIds.has(mesh.contextMeshId)) return;
        setRefreshingIds(current => new Set(current).add(mesh.contextMeshId));
        try {
            await refreshContextMeshAnalysis(token, mesh.contextMeshId);
            setMeshes(current => current.map(item => item.contextMeshId === mesh.contextMeshId ? {
                ...item,
                analysisSummary: {
                    status: "pending",
                    score: item.analysisSummary?.score ?? null,
                    tier: item.analysisSummary?.tier ?? null,
                    isStale: item.analysisSummary?.score != null,
                    currentSnapshotVersion: item.analysisSummary?.currentSnapshotVersion ?? null,
                    analyzedSnapshotVersion: item.analysisSummary?.analyzedSnapshotVersion ?? null
                }
            } : item));
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : "Unable to refresh analysis.");
        } finally {
            setRefreshingIds(current => {
                const next = new Set(current);
                next.delete(mesh.contextMeshId);
                return next;
            });
        }
    };

    const handleDelete = (mesh: ContextMeshWorkspace) => {
        if (!token) return;
        setDeletingMesh(mesh);
    };

    const handleCancelDelete = () => {
        if (isDeletingMesh) return;
        setDeletingMesh(null);
    };

    const handleConfirmDelete = async () => {
        if (!token || !deletingMesh) return;
        setIsDeletingMesh(true);
        setError(null);
        try {
            await deleteContextMesh(token, deletingMesh.contextMeshId);
            setMeshes(current => current.filter(item => item.contextMeshId !== deletingMesh.contextMeshId));
            setDeletingMesh(null);
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : "Unable to delete the Context Mesh.");
        } finally {
            setIsDeletingMesh(false);
        }
    };

    const handleDeleteDraft = (draft: ProductCreationDraftRecord) => {
        if (!token) return;
        setDeletingDraft(draft);
    };

    const handleConfirmDeleteDraft = async () => {
        if (!token || !deletingDraft) return;
        const draft = deletingDraft;
        setDeletingDraft(null);
        setError(null);
        try {
            await discardProductCreationDraft(token, draft.draft_id, { cleanupStaged: true });
            setDrafts(current => current.filter(item => item.draft_id !== draft.draft_id));
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : "Unable to delete the Context Mesh draft.");
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, bgcolor: "grey.50" }} data-testid="context-mesh-console">
            <NoticeBar />
            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: { xs: 2, md: 3.5 }, pt: 3, pb: 5 }}>
                <Box sx={{ mb: 2.25 }}>
                    <Stack direction="row" alignItems="baseline" spacing={1.25}>
                        <Typography component="h1" sx={{ fontSize: 24, lineHeight: 1.25, fontWeight: 600, letterSpacing: "-0.01em" }}>Context Mesh Hub</Typography>
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`${meshes.length} total`}
                            sx={{ height: 23, borderRadius: 999, bgcolor: "grey.50", color: "text.secondary", fontSize: 11, fontWeight: 600 }}
                        />
                    </Stack>
                    <Typography color="text.secondary" sx={{ mt: 0.5, fontSize: 13.5 }}>Start creating your standalone Context Meshes</Typography>
                </Box>

                <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} sx={{ mb: 2.5 }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search your Context Meshes"
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 17, color: "text.disabled" }} /></InputAdornment>,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Box aria-hidden="true" sx={{ px: 0.75, py: 0.2, border: `1px solid ${theme.palette.divider}`, borderRadius: "5px", bgcolor: "grey.50", color: "text.disabled", fontSize: 10.5, lineHeight: 1.3 }}>⌘K</Box>
                                </InputAdornment>
                            )
                        }}
                        sx={{ "& .MuiOutlinedInput-root": { height: 38, borderRadius: "9px", bgcolor: "background.paper", fontSize: 13 } }}
                    />
                    <Select
                        size="small"
                        value={sort}
                        onChange={event => setSort(event.target.value as HubSort)}
                        renderValue={value => <><Box component="span" sx={{ fontWeight: 600 }}>Sort</Box> <Box component="span" sx={{ fontWeight: 400 }}>{value === "updated-desc" ? "Last updated" : value === "updated-asc" ? "Oldest updated" : value === "name-asc" ? "Name A–Z" : value === "name-desc" ? "Name Z–A" : value === "score-desc" ? "Score high–low" : "Score low–high"}</Box></>}
                        sx={{ minWidth: 185, height: 38, borderRadius: "9px", bgcolor: "#DBE5FF", color: "#103699", fontSize: 13, "& .MuiOutlinedInput-notchedOutline": { borderColor: "#103699" } }}
                        inputProps={{ "aria-label": "Sort Context Meshes" }}
                    >
                        <MenuItem value="updated-desc">Updated: newest</MenuItem><MenuItem value="updated-asc">Updated: oldest</MenuItem>
                        <MenuItem value="name-asc">Name: A–Z</MenuItem><MenuItem value="name-desc">Name: Z–A</MenuItem>
                        <MenuItem value="score-desc">Score: high–low</MenuItem><MenuItem value="score-asc">Score: low–high</MenuItem>
                    </Select>
                    <Select
                        size="small"
                        value={statusFilter}
                        onChange={event => setStatusFilter(event.target.value as HubStatusFilter)}
                        renderValue={value => <><Box component="span" sx={{ fontWeight: 600 }}>Status</Box> <Box component="span" sx={{ fontWeight: 400, color: "text.secondary" }}>{value === "all" ? "All" : statusLabel(value)}</Box></>}
                        sx={{ minWidth: 145, height: 38, borderRadius: "9px", bgcolor: "background.paper", fontSize: 13 }}
                        inputProps={{ "aria-label": "Filter Context Meshes by status" }}
                    >
                        <MenuItem value="all">All statuses</MenuItem><MenuItem value="current">Current</MenuItem>
                        <MenuItem value="processing">Processing</MenuItem><MenuItem value="incomplete">Incomplete</MenuItem><MenuItem value="failed">Failed</MenuItem>
                    </Select>
                </Stack>

                <GetStartedHero
                    variant="context_mesh"
                    onStartProduct={onCreateContextMesh}
                    primaryActionLabel="Create a Context Mesh"
                    showPrimaryAction={canCreateMesh}
                />

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Paper
                    variant="outlined"
                    sx={{ borderRadius: "12px", overflow: "hidden", containerType: "inline-size", containerName: "context-mesh-list" }}
                    aria-label="Context Mesh list"
                    data-testid="context-mesh-list"
                >
                    <Box
                        sx={{
                            ...HUB_ROW_LAYOUT,
                            height: 38,
                            minHeight: 38,
                            bgcolor: "grey.50",
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            [HUB_ROW_COMPACT_QUERY]: { display: "none" }
                        }}
                    >
                        <Box sx={{ gridArea: "icon" }} />
                        <Typography sx={{ gridArea: "primary", minWidth: 0, fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", color: "text.disabled" }}>CONTEXT MESH</Typography>
                        <Typography sx={{ gridArea: "updated", fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", color: "text.disabled", textAlign: "right" }}>UPDATED</Typography>
                        <Typography sx={{ gridArea: "mesh", fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", color: "text.disabled", textAlign: "center" }}>MESH</Typography>
                        <Box sx={{ gridArea: "actions" }} />
                    </Box>

                    {canCreateMesh && (
                        <Box
                            role="button"
                            tabIndex={0}
                            aria-label="Create a Context Mesh"
                            onClick={onCreateContextMesh}
                            onKeyDown={event => {
                                if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    onCreateContextMesh();
                                }
                            }}
                            sx={{
                                ...HUB_ROW_LAYOUT,
                                cursor: "pointer",
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                background: "linear-gradient(90deg, rgba(25, 84, 237, 0.08) 0%, rgba(25, 84, 237, 0.02) 70%, transparent 100%)",
                                "&:hover": { background: "linear-gradient(90deg, rgba(25, 84, 237, 0.14) 0%, rgba(25, 84, 237, 0.05) 70%, transparent 100%)" },
                                "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 }
                            }}
                            data-testid="context-mesh-list-row"
                            data-row-kind="create"
                        >
                            <Box sx={{ gridArea: "icon", width: 26, height: 26, borderRadius: "7px", bgcolor: "#103699", color: "common.white", display: "grid", placeItems: "center" }}>
                                <BoltRoundedIcon sx={{ fontSize: 16 }} />
                            </Box>
                            <Box sx={HUB_PRIMARY_CELL}>
                                <Typography component="span" sx={{ fontSize: 13.5, fontWeight: 600, color: "#103699" }}>Create a Context Mesh</Typography>
                                <Typography component="span" color="text.secondary" sx={{ fontSize: 12.5, [HUB_ROW_COMPACT_QUERY]: { display: "none" } }}> · Add context and analyze an independent Context Mesh.</Typography>
                            </Box>
                            <Typography color="text.secondary" sx={{ ...HUB_COMPACT_META, fontSize: 12.5 }}>
                                Add context and analyze an independent Context Mesh.
                            </Typography>
                            <Box sx={{ gridArea: "updated", [HUB_ROW_COMPACT_QUERY]: { display: "none" } }} />
                            <Box sx={{ gridArea: "mesh" }} />
                            <Typography sx={{ gridArea: "actions", justifySelf: "end", color: "#103699", fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap" }}>Start →</Typography>
                        </Box>
                    )}

                    {visibleDrafts.map(draft => {
                        const name = draft.draft_state.state.fields.name.trim() || "Untitled Context Mesh";
                        const progress = productCreationDraftProgress(draft);
                        return (
                            <Box
                                key={`draft-${draft.draft_id}`}
                                role="button"
                                tabIndex={0}
                                aria-label={`Resume draft ${name}`}
                                onClick={() => onResumeContextMeshDraft(draft)}
                                onKeyDown={event => {
                                    if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        onResumeContextMeshDraft(draft);
                                    }
                                }}
                                sx={{
                                    ...HUB_ROW_LAYOUT,
                                    cursor: "pointer",
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                    bgcolor: DRAFT_BLUE.wash,
                                    "&:hover": { bgcolor: DRAFT_BLUE.soft },
                                    "&:focus-visible": { outline: `2px solid ${DRAFT_BLUE.main}`, outlineOffset: -2 }
                                }}
                                data-testid="context-mesh-list-row"
                                data-row-kind="draft"
                            >
                                <Box aria-hidden="true" sx={{ gridArea: "icon", width: 26, height: 26, borderRadius: "7px", bgcolor: DRAFT_BLUE.soft, color: DRAFT_BLUE.main, display: "grid", placeItems: "center" }}>
                                    <EditOutlinedIcon sx={{ fontSize: 16 }} />
                                </Box>
                                <Stack direction="row" alignItems="center" spacing={1} sx={HUB_PRIMARY_CELL} title={`${name} · ${progress.completed} of ${progress.total} sections complete`}>
                                    <Typography component="span" sx={{ minWidth: 0, maxWidth: "50%", fontSize: 13.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", [HUB_ROW_COMPACT_QUERY]: { maxWidth: "100%" } }}>{name}</Typography>
                                    <DraftBadge />
                                    <Typography component="span" color="text.secondary" sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", fontSize: 12.5, whiteSpace: "nowrap", [HUB_ROW_COMPACT_QUERY]: { display: "none" } }}>· {progress.completed} of {progress.total} sections complete</Typography>
                                </Stack>
                                <Typography color="text.secondary" sx={{ ...HUB_COMPACT_META, fontSize: 12 }}>
                                    {progress.completed} of {progress.total} sections complete · {new Date(draft.updated_at).toLocaleDateString()}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ gridArea: "updated", minWidth: 0, textAlign: "right", whiteSpace: "nowrap", [HUB_ROW_COMPACT_QUERY]: { display: "none" } }}>{new Date(draft.updated_at).toLocaleDateString()}</Typography>
                                <Box sx={{ gridArea: "mesh" }} />
                                <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5} sx={{ gridArea: "actions", minWidth: 0, whiteSpace: "nowrap" }} onClick={event => event.stopPropagation()}>
                                    <Button
                                        size="small"
                                        aria-label={`Continue creating ${name}`}
                                        onClick={() => onResumeContextMeshDraft(draft)}
                                        sx={{ minWidth: 0, justifyContent: "flex-end", px: 0.5, color: DRAFT_BLUE.main, fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}
                                    >
                                        <Box component="span" sx={{ [HUB_ROW_PHONE_QUERY]: { display: "none" } }}>Continue creating&nbsp;</Box>→
                                    </Button>
                                    <Tooltip title="Delete draft">
                                        <IconButton
                                            size="small"
                                            aria-label={`Delete draft ${name}`}
                                            onClick={() => void handleDeleteDraft(draft)}
                                        >
                                            <DeleteOutlineRoundedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Box>
                        );
                    })}

                    {loading ? <Box sx={{ display: "grid", placeItems: "center", py: 7 }}><CircularProgress size={30} /></Box>
                        : visibleMeshes.length === 0 && visibleDrafts.length === 0 ? (
                            <Box sx={{ p: 5, textAlign: "center" }}>
                                <Typography variant="h6">{meshes.length || drafts.length ? "No matching Context Meshes" : "No standalone Context Meshes yet"}</Typography>
                                <Typography color="text.secondary" sx={{ mt: 0.75 }}>{meshes.length || drafts.length ? "Try another search or status filter." : "Create your first Context Mesh to get started."}</Typography>
                            </Box>
                        ) : (
                        <Stack>
                            {visibleMeshes.map(mesh => {
                                const state = displayStatus(mesh);
                                const isHighlighted = highlightedContextMeshId === mesh.contextMeshId;
                                const canManage = mesh.accessRole === "owner" || mesh.accessRole === "editor";
                                const score = mesh.analysisSummary?.score;
                                return (
                                    <Box
                                        key={mesh.contextMeshId}
                                        ref={isHighlighted ? highlightedRef : undefined}
                                        tabIndex={canViewAnalysis ? 0 : undefined}
                                        role={canViewAnalysis ? "button" : undefined}
                                        onClick={canViewAnalysis ? () => void onOpenContextMesh(mesh) : undefined}
                                        onKeyDown={event => {
                                            if (canViewAnalysis && (event.key === "Enter" || event.key === " ")) {
                                                event.preventDefault();
                                                void onOpenContextMesh(mesh);
                                            }
                                        }}
                                        aria-label={canViewAnalysis ? `Open Context Mesh ${mesh.name}` : undefined}
                                        sx={{ ...HUB_ROW_LAYOUT, cursor: canViewAnalysis ? "pointer" : "default", borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: isHighlighted ? "#DBE5FF" : "background.paper", transition: "background-color 150ms ease", "&:hover": canViewAnalysis ? { bgcolor: "rgba(25, 84, 237, 0.07)" } : undefined, "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 } }}
                                        data-testid="context-mesh-list-row"
                                        data-row-kind="mesh"
                                    >
                                        <Box aria-hidden="true" sx={{ gridArea: "icon", width: 26, height: 26, borderRadius: "50%", bgcolor: "grey.50", color: "text.secondary", display: "grid", placeItems: "center" }}>
                                            <Box sx={{ width: 10, height: 10, border: "1.5px solid currentColor", borderRadius: "50%" }} />
                                        </Box>
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={0.5}
                                            sx={HUB_PRIMARY_CELL}
                                            title={[mesh.name, mesh.description].filter(Boolean).join(" · ")}
                                        >
                                            <Typography component="span" sx={{ minWidth: 0, maxWidth: mesh.description ? "45%" : "100%", overflow: "hidden", textOverflow: "ellipsis", fontSize: 13.5, fontWeight: 600, whiteSpace: "nowrap", [HUB_ROW_COMPACT_QUERY]: { maxWidth: "100%" } }}>{mesh.name}</Typography>
                                            {mesh.description && <Typography component="span" color="text.disabled" sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", fontSize: 12.5, whiteSpace: "nowrap", [HUB_ROW_COMPACT_QUERY]: { display: "none" } }}>· {mesh.description}</Typography>}
                                        </Stack>
                                        <Typography color="text.secondary" sx={{ ...HUB_COMPACT_META, fontSize: 12 }} title={mesh.description ?? undefined}>
                                            {mesh.description ? `${mesh.description} · ` : ""}{mesh.updatedAt ? new Date(mesh.updatedAt).toLocaleDateString() : "—"}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ gridArea: "updated", minWidth: 0, textAlign: "right", whiteSpace: "nowrap", [HUB_ROW_COMPACT_QUERY]: { display: "none" } }}>{mesh.updatedAt ? new Date(mesh.updatedAt).toLocaleDateString() : "—"}</Typography>
                                        <Box sx={{ gridArea: "mesh", display: "grid", placeItems: "center" }}>
                                            {canViewAnalysis && (
                                                <Tooltip title={statusLabel(state)}>
                                                    <Box sx={{ width: 30, height: 30, borderRadius: "50%", background: score == null ? "#DBE5FF" : `conic-gradient(#1954ED 0% ${Math.max(0, Math.min(100, score))}%, #DBE5FF ${Math.max(0, Math.min(100, score))}% 100%)`, p: "3px", flex: "0 0 auto" }}>
                                                        <Box sx={{ width: "100%", height: "100%", borderRadius: "50%", bgcolor: "background.paper", display: "grid", placeItems: "center", fontSize: 9.5, fontWeight: 800 }}>{score == null ? "—" : Math.round(score)}</Box>
                                                    </Box>
                                                </Tooltip>
                                            )}
                                        </Box>
                                        <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ gridArea: "actions", minWidth: 0 }} onClick={event => event.stopPropagation()}>
                                            {canManage && <>
                                                {canViewAnalysis && <Tooltip title="Refresh analysis"><span><IconButton size="small" aria-label="Refresh analysis" disabled={refreshingIds.has(mesh.contextMeshId)} onClick={() => void handleRefresh(mesh)}>{refreshingIds.has(mesh.contextMeshId) ? <CircularProgress size={17} /> : <RefreshRoundedIcon fontSize="small" />}</IconButton></span></Tooltip>}
                                                {onEditContextMesh && (
                                                    <Tooltip title="Edit Context Mesh">
                                                        <IconButton
                                                            size="small"
                                                            aria-label={`Edit Context Mesh ${mesh.name}`}
                                                            onClick={() => onEditContextMesh(mesh)}
                                                        >
                                                            <EditOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => void handleDelete(mesh)}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton></Tooltip>
                                            </>}
                                        </Stack>
                                    </Box>
                                );
                            })}
                        </Stack>
                    )}
                </Paper>
            </Box>

            {deletingDraft && (
                <ConfirmDialog
                    open
                    title="Delete draft?"
                    message={`Delete the draft "${deletingDraft.draft_state.state.fields.name.trim() || "Untitled Context Mesh"}"? This cannot be undone.`}
                    confirmLabel="Delete draft"
                    cancelLabel="Keep draft"
                    destructive
                    onConfirm={() => void handleConfirmDeleteDraft()}
                    onCancel={() => setDeletingDraft(null)}
                />
            )}

            <Dialog
                open={Boolean(deletingMesh)}
                onClose={handleCancelDelete}
                aria-labelledby="delete-context-mesh-title"
                aria-describedby="delete-context-mesh-description"
            >
                <DialogTitle id="delete-context-mesh-title">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <WarningAmberRoundedIcon color="warning" />
                        Delete Context Mesh
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Typography id="delete-context-mesh-description">
                        Are you sure you want to delete{" "}
                        <Box component="span" sx={{ fontWeight: 600 }}>
                            {deletingMesh?.name || "this Context Mesh"}
                        </Box>
                        ? This action cannot be undone. The Context Mesh and its associated context will be permanently removed.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDelete} disabled={isDeletingMesh}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => void handleConfirmDelete()}
                        color="error"
                        variant="contained"
                        autoFocus
                        disabled={isDeletingMesh}
                        startIcon={isDeletingMesh ? <CircularProgress size={16} color="inherit" /> : undefined}
                    >
                        {isDeletingMesh ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
